import type {
  RolePermissionRequest,
  RolePermissionRequestOptions,
} from '@zhiyue/core';
import { RolePermissionManager } from '@zhiyue/core';
import React, { useEffect } from 'react';
import { createAppRequest } from '@/services/request';

const request = createAppRequest();
const ROLE_CREATE_URL = '/bff/iam-ui/roles';
const ROLE_CODE_PREFIX = 'ROLE';
const MAX_ROLE_CODE_LENGTH = 64;
const FALLBACK_ROLE_NAME_CODE = 'CUSTOM';

type RolePayload = {
  code?: string;
  description?: string;
  name?: string;
  [key: string]: unknown;
};

interface RolePermissionManagerWithAutoCodeProps {
  namespace?: string | null;
}

const commonChineseEnglishMap: Record<string, string> = {
  OSMP系统管理员: 'OSMP_SYSTEM_ADMINISTRATOR',
  物资主数据管理员: 'MATERIAL_MASTER_DATA_ADMINISTRATOR',
  库存管理员: 'INVENTORY_ADMINISTRATOR',
  规则管理员: 'RULE_ADMINISTRATOR',
  审批管理员: 'APPROVAL_ADMINISTRATOR',
  超级管理员: 'SUPER_ADMINISTRATOR',
  高级管理员: 'SENIOR_ADMINISTRATOR',
  系统管理员: 'SYSTEM_ADMINISTRATOR',
  管理员: 'ADMINISTRATOR',
  普通用户: 'GENERAL_USER',
  申请人: 'APPLICANT',
  审批人: 'APPROVER',
  操作员: 'OPERATOR',
  观察员: 'OBSERVER',
  审计员: 'AUDITOR',
  维护员: 'MAINTAINER',
  负责人: 'OWNER',
  主数据: 'MASTER_DATA',
  物资: 'MATERIAL',
  耗材: 'CONSUMABLE',
  办公: 'OFFICE',
  办公用品: 'OFFICE_SUPPLY',
  采购: 'PROCUREMENT',
  仓库: 'WAREHOUSE',
  库存: 'INVENTORY',
  供应商: 'SUPPLIER',
  财务: 'FINANCE',
  行政: 'ADMINISTRATION',
  人事: 'HUMAN_RESOURCES',
  业务: 'BUSINESS',
  运营: 'OPERATION',
  系统: 'SYSTEM',
  平台: 'PLATFORM',
  权限: 'PERMISSION',
  角色: 'ROLE',
  用户: 'USER',
  部门: 'DEPARTMENT',
  成员: 'MEMBER',
  员工: 'EMPLOYEE',
  专员: 'SPECIALIST',
  主管: 'SUPERVISOR',
  经理: 'MANAGER',
  主任: 'DIRECTOR',
  总监: 'DIRECTOR',
  高级: 'SENIOR',
  超级: 'SUPER',
  普通: 'GENERAL',
  管理: 'MANAGEMENT',
  维护: 'MAINTENANCE',
  配置: 'CONFIGURATION',
  规则: 'RULE',
  流程: 'WORKFLOW',
  审批: 'APPROVAL',
  申请: 'REQUEST',
  领用: 'RECEIVE',
  发放: 'ISSUE',
  盘点: 'STOCKTAKE',
  调拨: 'TRANSFER',
  统计: 'STATISTICS',
  报表: 'REPORT',
  查看: 'VIEWER',
  编辑: 'EDITOR',
  新增: 'CREATOR',
  删除: 'DELETER',
  类目: 'CATEGORY',
  单位: 'UNIT',
  品牌: 'BRAND',
};

const normalizeAsciiRoleNamePart = (value: string) =>
  value
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^A-Za-z]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '')
    .toUpperCase();

const isCjkCharacter = (value: string) => /[\u4e00-\u9fff]/.test(value);

const resolveChineseWord = (value: string, startIndex: number) => {
  const matchedWord = Object.keys(commonChineseEnglishMap)
    .sort((left, right) => right.length - left.length)
    .find((word) => value.startsWith(word, startIndex));

  if (!matchedWord) return null;

  return {
    code: commonChineseEnglishMap[matchedWord],
    length: matchedWord.length,
  };
};

const tokenizeRoleName = (roleName?: string) => {
  const value = roleName?.trim() || '';
  const tokens: string[] = [];
  let asciiBuffer = '';

  const pushToken = (token: string) => {
    if (tokens[tokens.length - 1] !== token) tokens.push(token);
  };

  const flushAsciiBuffer = () => {
    const normalizedAscii = normalizeAsciiRoleNamePart(asciiBuffer);
    if (normalizedAscii) pushToken(normalizedAscii);
    asciiBuffer = '';
  };

  for (let index = 0; index < value.length; index += 1) {
    const current = value[index];
    const chineseWord = resolveChineseWord(value, index);

    if (chineseWord) {
      flushAsciiBuffer();
      pushToken(chineseWord.code);
      index += chineseWord.length - 1;
      continue;
    }

    if (isCjkCharacter(current)) {
      flushAsciiBuffer();
      pushToken(FALLBACK_ROLE_NAME_CODE);
      continue;
    }

    asciiBuffer += current;
  }

  flushAsciiBuffer();

  return tokens;
};

const buildGeneratedRoleCode = (roleName?: string) => {
  const normalizedName = tokenizeRoleName(roleName).join('_');
  const base = `${ROLE_CODE_PREFIX}_${normalizedName || FALLBACK_ROLE_NAME_CODE}`;
  const trimmedBase = base.slice(0, MAX_ROLE_CODE_LENGTH).replace(/_+$/g, '');

  return trimmedBase;
};

const isPlainRolePayload = (data: unknown): data is RolePayload =>
  !!data && typeof data === 'object' && !Array.isArray(data);

const ensureRoleCode = (data: unknown): unknown => {
  if (!isPlainRolePayload(data)) return data;
  if (typeof data.code === 'string' && data.code.trim()) return data;

  return {
    ...data,
    code: buildGeneratedRoleCode(data.description || data.name),
  };
};

const normalizeRoleCreateOptions = (
  url: string,
  options?: RolePermissionRequestOptions,
): RolePermissionRequestOptions | undefined => {
  if (url !== ROLE_CREATE_URL || options?.method !== 'POST') return options;

  return {
    ...options,
    data: ensureRoleCode(options.data),
  };
};

const rolePermissionRequest: RolePermissionRequest = (url, options) => {
  const normalizedOptions = normalizeRoleCreateOptions(url, options);

  return request(url, {
    method: normalizedOptions?.method,
    params: normalizedOptions?.params as Record<string, unknown> | undefined,
    data: normalizedOptions?.data,
    headers: normalizedOptions?.headers,
  });
};

const getInputByLabel = (container: HTMLElement, labelText: string) => {
  const labels = Array.from(container.querySelectorAll('label'));
  const label = labels.find((item) =>
    item.textContent?.trim().includes(labelText),
  );
  if (!label) return null;

  const inputId = label.getAttribute('for');
  if (inputId) {
    const input = document.getElementById(inputId);
    if (input instanceof HTMLInputElement && container.contains(input)) {
      return input;
    }
  }

  return label
    .closest('.ant-form-item')
    ?.querySelector<HTMLInputElement>('input');
};

const setNativeInputValue = (input: HTMLInputElement, value: string) => {
  if (input.value === value) return;

  const valueSetter = Object.getOwnPropertyDescriptor(input, 'value')?.set;
  const prototypeValueSetter = Object.getOwnPropertyDescriptor(
    window.HTMLInputElement.prototype,
    'value',
  )?.set;

  if (prototypeValueSetter && valueSetter !== prototypeValueSetter) {
    prototypeValueSetter.call(input, value);
  } else if (valueSetter) {
    valueSetter.call(input, value);
  } else {
    input.value = value;
  }

  input.dispatchEvent(new Event('input', { bubbles: true }));
  input.dispatchEvent(new Event('change', { bubbles: true }));
};

const syncCreateRoleModalCode = () => {
  const modals = Array.from(
    document.querySelectorAll<HTMLElement>('.ant-modal'),
  );

  for (const modal of modals) {
    const title = modal.querySelector('.ant-modal-title')?.textContent?.trim();
    if (title !== '新建角色') continue;

    const nameInput = getInputByLabel(modal, '角色名称');
    const codeInput = getInputByLabel(modal, '角色标识');
    if (!codeInput || codeInput.disabled) continue;

    codeInput.readOnly = true;
    codeInput.placeholder = '由系统自动生成';
    codeInput.setAttribute('aria-readonly', 'true');
    codeInput.setAttribute('title', '由角色名称自动生成');
    setNativeInputValue(codeInput, buildGeneratedRoleCode(nameInput?.value));
  }
};

const useAutoRoleCodeInModal = () => {
  useEffect(() => {
    if (typeof document === 'undefined') return;

    const scheduleSync = () => window.setTimeout(syncCreateRoleModalCode, 0);
    const observer = new MutationObserver(scheduleSync);

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
    document.addEventListener('input', scheduleSync, true);
    document.addEventListener('change', scheduleSync, true);
    scheduleSync();

    return () => {
      observer.disconnect();
      document.removeEventListener('input', scheduleSync, true);
      document.removeEventListener('change', scheduleSync, true);
    };
  }, []);
};

const RolePermissionManagerWithAutoCode: React.FC<
  RolePermissionManagerWithAutoCodeProps
> = ({ namespace }) => {
  useAutoRoleCodeInModal();

  return (
    <RolePermissionManager
      request={rolePermissionRequest}
      namespace={namespace}
    />
  );
};

export default RolePermissionManagerWithAutoCode;
